import { REHYDRATE } from 'redux-persist';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';

import { ICampaign } from '../../../types/campaigns';
import { CreateSendMethodEnum } from '../../../types/createSendFlow';
import { ShippingModeEnum } from '../../../types/createSendRecipients';
import { computeLinkShippingSeed, linkShippingSeedNeedsAddressBook } from '../../../utils/createSendLink';
import {
  fetchAddressBookRequest,
  FETCH_ADDRESS_BOOK_FAILURE,
  FETCH_ADDRESS_BOOK_SUCCESS,
} from '../../actions/addressBook';
import { selectAddressBook, selectIsAddressBookLoading } from '../../selectors/addressBook';
import { SET_CS_GIFT_CHOICE_MODE, TOGGLE_CS_KIT } from '../actions/contents';
import {
  CS_CLEAR_RECIPIENTS,
  CS_REMOVE_RECIPIENT,
  CS_SET_RECIPIENTS,
  setClaimAddressIds,
  setClaimLinkMode,
  setMultiKitAddressMode,
  setOverrideAddress,
} from '../actions/recipients';
import { selectCsKits } from '../selectors/contents';
import { selectCreateSendMethod } from '../selectors/method';
import {
  selectCsAddressMode,
  selectCsClaimLinkMode,
  selectCsMultiKitAddressMode,
  selectCsOverrideAddress,
  selectCsRecipients,
  selectEffectiveShipping,
  selectIsClaimForced,
  selectIsMultiKit,
  selectKitsCount,
} from '../selectors/recipients';

export function* clearStaleOverrideWorker(): Generator<any, any, any> {
  const { mode } = yield select(selectEffectiveShipping);

  if (mode === ShippingModeEnum.FixedAddress) {
    return;
  }

  const kitsCount = yield select(selectKitsCount);
  const recipients = yield select(selectCsRecipients);
  const overrideAddress = yield select(selectCsOverrideAddress);

  if (kitsCount < 2 && recipients.length < 2 && overrideAddress) {
    yield put(setOverrideAddress(null));
  }
}

export function* syncModesToKitCount(isMultiKit: boolean): Generator<any, any, any> {
  if (isMultiKit) {
    const isClaimForced = yield select(selectIsClaimForced);

    if (isClaimForced) {
      return;
    }

    const addressMode = yield select(selectCsAddressMode);
    const claimLinkMode = yield select(selectCsClaimLinkMode);
    const next =
      addressMode === ShippingModeEnum.ClaimLink && claimLinkMode === ShippingModeEnum.FromList
        ? ShippingModeEnum.FromList
        : ShippingModeEnum.RecipientDecides;
    yield put(setMultiKitAddressMode(next));
    return;
  }

  const multiKitMode = yield select(selectCsMultiKitAddressMode);
  yield put(
    setClaimLinkMode(
      multiKitMode === ShippingModeEnum.FromList ? ShippingModeEnum.FromList : ShippingModeEnum.RecipientDecides,
    ),
  );
}

export function* seedLinkShippingFromKitWorker(): Generator<any, any, any> {
  const method = yield select(selectCreateSendMethod);
  if (method !== CreateSendMethodEnum.Link) {
    return;
  }

  const kits: ICampaign[] = (yield select(selectCsKits)) ?? [];
  if (kits.length !== 1) {
    return;
  }

  const kit = kits[0];
  let addressBook = yield select(selectAddressBook);

  if (!addressBook && linkShippingSeedNeedsAddressBook(kit)) {
    const isLoading = yield select(selectIsAddressBookLoading);
    if (!isLoading) {
      yield put(fetchAddressBookRequest());
      const resultAction = yield take([FETCH_ADDRESS_BOOK_SUCCESS, FETCH_ADDRESS_BOOK_FAILURE]);
      if (resultAction.type !== FETCH_ADDRESS_BOOK_SUCCESS) {
        return;
      }
      addressBook = yield select(selectAddressBook);
    }
  }

  const seed = yield call(computeLinkShippingSeed, kit, addressBook ?? undefined);

  yield put(setMultiKitAddressMode(seed.mode));
  yield put(setClaimAddressIds(seed.claimAddressIds));
  yield put(setOverrideAddress(seed.overrideAddress));
}

const sagas = {
  *watchLinkKitShippingSeed(): Generator<any, any, any> {
    yield takeLatest(TOGGLE_CS_KIT, seedLinkShippingFromKitWorker);
  },

  *watchKitCountBoundary(): Generator<any, any, any> {
    let prevIsMultiKit: boolean = yield select(selectIsMultiKit);

    while (true) {
      const action = yield take([TOGGLE_CS_KIT, SET_CS_GIFT_CHOICE_MODE, REHYDRATE]);
      const isMultiKit: boolean = yield select(selectIsMultiKit);

      if (action.type === REHYDRATE) {
        prevIsMultiKit = isMultiKit;
        continue;
      }

      if (isMultiKit !== prevIsMultiKit) {
        yield call(syncModesToKitCount, isMultiKit);
      }
      yield call(clearStaleOverrideWorker);
      prevIsMultiKit = isMultiKit;
    }
  },

  *watchRecipientOverrideCleanup(): Generator<any, any, any> {
    while (true) {
      yield take([CS_REMOVE_RECIPIENT, CS_CLEAR_RECIPIENTS, CS_SET_RECIPIENTS]);
      yield call(clearStaleOverrideWorker);
    }
  },
};

export default sagas;
